import React, { useEffect, useState } from "react";
import {
    Container,
    VStack,
    Text,
    Spinner,
    Button,
    Icon,
    Heading,
} from "@chakra-ui/react";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase/firebase-config";
import { getStripeMode } from "../lib/stripeMode";

export const HostOnboarding = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [user, loading] = useAuthState(auth);
    const [status, setStatus] = useState("loading");
    const [message, setMessage] = useState("Loading...");
    const [hasVerified, setHasVerified] = useState(false);
    // Venue partners set up payouts without becoming event producers, so
    // they come back tagged `role=venue` and get marked as venue owners
    // instead of hosts.
    const isVenueRole = searchParams.get("role") === "venue";

    useEffect(() => {
        // Wait for auth to finish loading
        if (loading) {
            setMessage("Checking authentication...");
            return;
        }

        // If no user after loading, redirect to login
        if (!user) {
            navigate("/login");
            return;
        }

        // Only verify once
        if (hasVerified) {
            return;
        }

        const success = searchParams.get("success");
        const refresh = searchParams.get("refresh");
        const returnTo = searchParams.get("returnTo");

        if (refresh) {
            setStatus("refresh");
            setMessage(
                isVenueRole
                    ? "Please complete your Stripe onboarding so your venue can get paid."
                    : "Please complete your Stripe onboarding to become a host."
            );
            return;
        }

        if (!success) {
            setStatus("error");
            setMessage("Invalid onboarding session.");
            return;
        }

        // Start verification
        setHasVerified(true);
        verifyAccount();

        async function verifyAccount() {
            setStatus("loading");
            setMessage("Processing your Stripe account... Please wait.");

            try {
                // Wait 3 seconds initially for Firestore to sync
                await new Promise(r => setTimeout(r, 3000));
                
                // Get accountId from Firestore
                const userDoc = await getDoc(doc(db, "users", user.uid));
                const userData = userDoc.data();
                let accountId = userData?.accountId || userData?.stripeAccountId;
                
                console.log("📊 User data:", { accountId, stripeAccountId: userData?.stripeAccountId });

                // If not found, wait a bit more and try again
                if (!accountId) {
                    setMessage("Still syncing... Please wait.");
                    await new Promise(r => setTimeout(r, 3000));
                    
                    const retryDoc = await getDoc(doc(db, "users", user.uid));
                    const retryData = retryDoc.data();
                    accountId = retryData?.accountId || retryData?.stripeAccountId;
                    console.log("📊 Retry user data:", { accountId });
                }

                // If still not found, show friendly message with button
                if (!accountId) {
                    console.log("❌ No accountId found");
                    setStatus("pending");
                    setMessage("Your account is still being set up. Click below to check again.");
                    return;
                }

                // Check Stripe status
                setMessage("Checking Stripe account status...");

                const callable = httpsCallable(functions, "checkStripeAccountStatus");
                const { data: result } = await callable({
                    accountId,
                    mode: getStripeMode(),
                });
                console.log("📊 Stripe status:", result);

                if (result?.accountStatus?.detailsSubmitted) {
                    await setDoc(
                        doc(db, "users", user.uid),
                        {
                            ...(isVenueRole
                                ? { isVenueOwner: true, venueOwnerSince: Timestamp.now() }
                                : { isHost: true, hostSince: Timestamp.now() }),
                            stripeAccountStatus: result.isComplete ? "complete" : "pending",
                        },
                        { merge: true }
                    );

                    setStatus("success");
                    setMessage(
                        isVenueRole
                            ? "Your payout account is ready — let's get your venue listed!"
                            : "Congratulations! You're now a host on Proximy!"
                    );
                    // Send them back wherever they started (e.g. the venue
                    // landing page) so that flow can pick up automatically,
                    // instead of always dumping them on the homepage.
                    const destination = returnTo
                        ? `${returnTo}${returnTo.includes("?") ? "&" : "?"}openVenue=true`
                        : "/";
                    setTimeout(() => navigate(destination), 3000);
                } else {
                    setStatus("pending");
                    setMessage("Your Stripe account is still being set up. Click 'Check Again' to retry.");
                }
            } catch (error) {
                console.error("❌ Error:", error);
                setStatus("error");
                setMessage("Failed to verify your account. Please try again.");
            }
        }
    }, [user, loading, searchParams, navigate, hasVerified, isVenueRole]);

    const handleCheckAgain = () => {
        setHasVerified(false);
        setStatus("loading");
        setMessage("Checking again...");
    };

    const handleRetry = () => {
        const returnTo = searchParams.get("returnTo");
        const resume = isVenueRole ? "openVenue=true" : "openHost=true";
        navigate(returnTo ? `${returnTo}?${resume}` : `/?${resume}`);
    };

    return (
        <Container maxW="container.sm" py={20}>
            <VStack spacing={6} textAlign="center">
                {status === "loading" && (
                    <>
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                        <Heading size="md">{message}</Heading>
                    </>
                )}

                {status === "success" && (
                    <>
                        <Icon as={FaCheckCircle} boxSize={20} color="green.500" />
                        <Heading size="lg">
                            {isVenueRole ? "You're all set! 🎉" : "Welcome, Host! 🎉"}
                        </Heading>
                        <Text fontSize="lg" color="gray.600">{message}</Text>
                        <Text fontSize="sm" color="gray.500">Redirecting...</Text>
                    </>
                )}

                {status === "pending" && (
                    <>
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                        <Heading size="lg">Almost There!</Heading>
                        <Text fontSize="lg" color="gray.600">{message}</Text>
                        <Button colorScheme="blue" size="lg" mt={4} onClick={handleCheckAgain}>
                            Check Again
                        </Button>
                    </>
                )}

                {status === "error" && (
                    <>
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                        <Heading size="lg">Verifying Connection</Heading>
                        <Text fontSize="lg" color="gray.600">
                            Let's make sure everything is set up correctly.
                        </Text>
                        <VStack spacing={3} pt={4}>
                            <Button colorScheme="blue" size="lg" onClick={handleCheckAgain}>
                                Test Connection
                            </Button>
                            <Button variant="ghost" onClick={() => navigate("/")}>
                                Go Home
                            </Button>
                        </VStack>
                    </>
                )}

                {status === "refresh" && (
                    <>
                        <Icon as={FaExclamationTriangle} boxSize={20} color="orange.500" />
                        <Heading size="lg">Almost There!</Heading>
                        <Text fontSize="lg" color="gray.600">{message}</Text>
                        <VStack spacing={3}>
                            <Button colorScheme="blue" onClick={handleRetry}>
                                Continue Setup
                            </Button>
                            <Button variant="ghost" onClick={() => navigate("/")}>
                                Do This Later
                            </Button>
                        </VStack>
                    </>
                )}
            </VStack>
        </Container>
    );
};
